import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import axios from '../../../../services/ApiService';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { AssessmentData } from '../../../types/AssessmentTypes';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { paths } from '../../../../utils/paths';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';

interface AssessmentViewProps {
  userType: 'BUSINESS' | 'AGENT' | 'COMPANY';
}

function AssessmentView({ userType }: AssessmentViewProps) {
  const [assessments, setAssessments] = useState<AssessmentData[]>([]);
  const [filteredAssessments, setFilteredAssessments] = useState<AssessmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();
  const { user } = useAuthContext();

  useEffect(() => {
    fetchAssessments();
  }, []);

  useEffect(() => {
    filterAssessments();
  }, [searchTerm, statusFilter, assessments]);

  const fetchAssessments = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get('/getAssessmentDataList');
      const filteredAssessments = response.data.filter(
        assessment => assessment.userType === userType
      );
      setAssessments(filteredAssessments);
      setFilteredAssessments(filteredAssessments);
    } catch (error) {
      console.error('Error fetching assessments.');
    } finally {
      setLoading(false);
    }
  };

  const filterAssessments = () => {
    let filtered = [...assessments];

    // Apply both search and status filters
    filtered = filtered.filter((assessment) => {
      const matchesSearch = !searchTerm || 
        assessment.assessmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (assessment.workflowName || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'ALL' || assessment.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    setFilteredAssessments(filtered);
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case BVTConstants.COMPLETED: return 'success';
      case BVTConstants.IN_PROGRESS: return 'warning';
      case BVTConstants.PENDING: return 'default';
      default: return 'default';
    }
  };

  const handleAssessmentAction = (assessment: AssessmentData) => {
    switch (assessment.status) {
      case BVTConstants.COMPLETED:
        navigate(`/${userType.toLowerCase()}` + paths.common.assessmentDetails, { 
          state: { trackerId: assessment.trackerId } 
        });
        break;
      case BVTConstants.IN_PROGRESS:
      case BVTConstants.PENDING:
        navigate(`/${userType.toLowerCase()}` + paths.common.assessmentForm, { 
          state: { trackerId: assessment.trackerId } 
        });
        break;
    }
  };

  const getActionButton = (assessment: AssessmentData) => {
    switch (assessment.status) {
      case BVTConstants.COMPLETED:
        return (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleAssessmentAction(assessment)}
          >
            View
          </Button>
        );
      case BVTConstants.IN_PROGRESS:
        return (
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleAssessmentAction(assessment)}
          >
            Resume
          </Button>
        );
      case BVTConstants.PENDING:
        return (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleAssessmentAction(assessment)}
          >
            Start
          </Button>
        );
    }
  };

  const truncateText = (text: string | undefined, limit: number) => {
    if (!text) return '-';
    if (text.length <= limit) return text;
    return text.substring(0, limit) + '..';
  };

  const handleSort = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    
    const sorted = [...filteredAssessments].sort((a, b) => {
      const aDate = new Date(a.lastUpdated).getTime();
      const bDate = new Date(b.lastUpdated).getTime();
      return newDirection === 'asc' ? aDate - bDate : bDate - aDate;
    });
    
    setFilteredAssessments(sorted);
  };

  const resultColorMap: Record<string, string> = {
    [BVTConstants.SCORE_RANGE_POOR]: '#F44336',
    [BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT]: '#FF9800',
    [BVTConstants.SCORE_RANGE_STRONG]: '#8BC34A',
    [BVTConstants.SCORE_RANGE_EXCELLENT]: '#4CAF50',
  };

  return (
    <>
      <Helmet>
        <title>{`BVT ${userType} - Assessments`}</title>
      </Helmet>
      <Box sx={{ padding: 3 }}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h5">
                My Assessments
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search assessments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                />
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value={BVTConstants.COMPLETED}>Completed</MenuItem>
                    <MenuItem value={BVTConstants.IN_PROGRESS}>In Progress</MenuItem>
                    <MenuItem value={BVTConstants.PENDING}>Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Assessment Name</TableCell>
                      <TableCell>Workflow Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={true}
                          direction={sortDirection}
                          onClick={handleSort}
                        >
                          Last Updated
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAssessments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="textSecondary">
                            {loading ? '' : 'No assessments found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAssessments.map((assessment) => (
                        <TableRow key={assessment.trackerId}>
                          <TableCell>{assessment.assessmentName}</TableCell>
                          <TableCell>
                            <Tooltip title={assessment.workflowName || '-'} arrow>
                              <span>{truncateText(assessment.workflowName, 10)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={assessment.status}
                              color={getStatusChipColor(assessment.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {assessment.status === 'COMPLETED' 
                              ? `${assessment.scoredPoints}/${assessment.totalScore}`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {assessment.status === 'COMPLETED' ? (
                              resultColorMap[assessment.result as keyof typeof resultColorMap] ? (
                                <Chip
                                  label={assessment.result || '-'}
                                  sx={{
                                    backgroundColor: resultColorMap[assessment.result as keyof typeof resultColorMap],
                                    color: '#fff',
                                    fontWeight: 600,
                                  }}
                                  size="small"
                                />
                              ) : (
                                <Chip label={assessment.result || '-'} size="small" />
                              )
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(assessment.lastUpdated).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            {getActionButton(assessment)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <LoadingSpinner loading={loading} />
    </>
  );
}

export default AssessmentView; 